import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { auth, functions, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useTheme } from "../contexts/ThemeContext";
import {
  Plus,
  Camera,
  Upload,
  X,
  Loader,
  Save,
  CheckCircle,
} from "lucide-react";

const RegisterPetPage = ({
  user,
  onPetRegistered,
  onComplete = null,
  isModal = false,
}) => {
  const { themeClasses } = useTheme();
  const [registrationData, setRegistrationData] = useState({
    name: "",
    species: "Dog",
    birthDate: "",
    gender: "",
    description: "",
    breed: "",
    breedConfidence: 0,
    photos: {
      frontFace: { file: null, preview: null, required: true },
      leftProfile: { file: null, preview: null, required: true },
      rightProfile: { file: null, preview: null, required: true },
      fullBody: { file: null, preview: null, required: false },
      distinctiveMarks: { file: null, preview: null, required: false },
    },
  });

  const [isRegistering, setIsRegistering] = useState(false);
  const [isDetectingBreed, setIsDetectingBreed] = useState(false);

  // Helper function to extract JPEG preview from DNG files
  const extractDngPreview = async (file) => {
    try {
      console.log(
        `Attempting to extract preview from ${file.name} (${file.size} bytes)`
      );
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const foundPreviews = [];

      // Look for all JPEG SOI markers (0xFF 0xD8) and collect all previews
      for (let i = 0; i < uint8Array.length - 1; i++) {
        if (uint8Array[i] === 0xff && uint8Array[i + 1] === 0xd8) {
          // Found JPEG start, now find the end (0xFF 0xD9)
          for (let j = i + 2; j < uint8Array.length - 1; j++) {
            if (uint8Array[j] === 0xff && uint8Array[j + 1] === 0xd9) {
              // Found JPEG end, extract the JPEG data
              const jpegData = uint8Array.slice(i, j + 2);

              // Skip very small previews (likely tiny thumbnails)
              if (jpegData.length > 10000) {
                // 10KB minimum
                foundPreviews.push({
                  data: jpegData,
                  size: jpegData.length,
                  start: i,
                });
                console.log(
                  `Found JPEG preview: ${jpegData.length} bytes at position ${i}`
                );
              }
              i = j + 2; // Skip past this JPEG to avoid overlaps
              break;
            }
          }
        }
      }

      console.log(
        `Found ${foundPreviews.length} potential JPEG previews in ${file.name}`
      );

      // Sort by size (largest first) and try each one
      foundPreviews.sort((a, b) => b.size - a.size);

      for (let preview of foundPreviews) {
        try {
          console.log(
            `Testing JPEG preview: ${preview.size} bytes from ${file.name}`
          );
          const blob = new Blob([preview.data], { type: "image/jpeg" });
          const url = URL.createObjectURL(blob);

          // Test if the blob is actually a valid image
          const isValid = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              console.log(
                `✅ Valid preview found in ${file.name}: ${img.width}x${img.height} (${preview.size} bytes)`
              );
              resolve(true);
            };
            img.onerror = () => {
              console.warn(
                `❌ Invalid preview in ${file.name} (${preview.size} bytes)`
              );
              URL.revokeObjectURL(url);
              resolve(false);
            };
            img.src = url;
          });

          if (isValid) {
            return url; // Return the first valid preview
          }
        } catch (error) {
          console.warn(`Error testing preview from ${file.name}:`, error);
        }
      }

      console.log(`No valid JPEG previews found in ${file.name}`);
      return null;
    } catch (error) {
      console.error(`Failed to extract DNG preview from ${file.name}:`, error);
      return null;
    }
  };

  const handleEnhancedPhotoUpload = async (event, photoType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Expanded supported types to include RAW formats
    const supportedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
      "image/x-adobe-dng", // DNG files
      "image/tiff", // Some DNG files report as TIFF
      "application/octet-stream", // Some DNG files report as this
    ];

    // Check file extension for DNG files (since MIME type can be inconsistent)
    const fileName = file.name.toLowerCase();
    const isDngFile =
      fileName.endsWith(".dng") ||
      fileName.endsWith(".cr2") ||
      fileName.endsWith(".nef") ||
      fileName.endsWith(".arw");
    const isSupportedMimeType = supportedTypes.includes(
      file.type.toLowerCase()
    );

    if (!isSupportedMimeType && !isDngFile) {
      alert(
        `This file format is not supported. Please use a common image format like JPEG or PNG.`
      );
      event.target.value = ""; // Clear the input
      return;
    }

    try {
      // Handle DNG files - extract embedded JPEG preview
      if (isDngFile) {
        console.log(`Processing DNG file: ${file.name}`);

        // Try to extract JPEG preview from DNG file
        console.log(
          `Starting DNG preview extraction for ${photoType}: ${file.name}`
        );
        const dngPreview = await extractDngPreview(file);
        console.log(
          `DNG preview extraction result for ${photoType}:`,
          dngPreview ? "Success" : "Failed"
        );

        setRegistrationData((prev) => ({
          ...prev,
          photos: {
            ...prev.photos,
            [photoType]: {
              ...prev.photos[photoType],
              file: file,
              preview: dngPreview, // Use extracted preview or null
              isDng: true, // Flag to show different UI
              fileName: file.name, // Store filename for display
              fileSize: file.size, // Store file size for display
            },
          },
        }));

        // Try breed detection for DNG front face photos using server-side processing
        if (photoType === "frontFace" && registrationData.species === "Dog") {
          console.log(
            "DNG front face uploaded - breed detection will be attempted server-side"
          );
          setIsDetectingBreed(true);
          try {
            // Upload the DNG file to storage first, then process it
            const timestamp = Date.now();
            const filename = `temp-breed-detection/${user.uid}/${timestamp}-${file.name}`;
            const storageRef = ref(storage, filename);

            console.log("Uploading DNG file for breed detection...");
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Use a server-side function that can handle DNG files
            console.log("Processing DNG file for breed detection...");
            const detectBreedFromUrlFn = httpsCallable(
              functions,
              "detectDogBreedFromUrl"
            );
            const response = await detectBreedFromUrlFn({
              imageUrl: downloadURL,
            });

            if (response.data.success) {
              const breedData = response.data.data;
              setRegistrationData((prev) => ({
                ...prev,
                breed: breedData.breed,
                breedConfidence: breedData.confidence,
              }));
              console.log(
                `Breed detected from DNG: ${breedData.breed} (${Math.round(
                  breedData.confidence * 100
                )}%)`
              );
            }
          } catch (error) {
            console.error("DNG breed detection failed:", error);
            // Don't show error to user - just continue without breed detection
          } finally {
            setIsDetectingBreed(false);
          }
        }
      } else {
        // Handle regular image files (JPEG, PNG, etc.)
        const imageUrl = URL.createObjectURL(file);
        setRegistrationData((prev) => ({
          ...prev,
          photos: {
            ...prev.photos,
            [photoType]: {
              ...prev.photos[photoType],
              file: file,
              preview: imageUrl,
              isDng: false,
              fileName: file.name, // Store filename for display
              fileSize: file.size, // Store file size for display
            },
          },
        }));

        // Auto-detect breed only from front face photo (non-DNG) - use URL-based approach
        if (photoType === "frontFace" && registrationData.species === "Dog") {
          setIsDetectingBreed(true);
          try {
            // Upload the file to storage first, then process it via URL
            const timestamp = Date.now();
            const filename = `temp-breed-detection/${user.uid}/${timestamp}-${file.name}`;
            const storageRef = ref(storage, filename);

            console.log("Uploading image for breed detection...");
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Use URL-based breed detection (same as DNG approach)
            console.log("Processing image for breed detection...");
            const detectBreedFromUrlFn = httpsCallable(
              functions,
              "detectDogBreedFromUrl"
            );
            const response = await detectBreedFromUrlFn({
              imageUrl: downloadURL,
            });

            if (response.data.success) {
              const breedData = response.data.data;
              setRegistrationData((prev) => ({
                ...prev,
                breed: breedData.breed,
                breedConfidence: breedData.confidence,
              }));
              console.log(
                `Breed detected: ${breedData.breed} (${Math.round(
                  breedData.confidence * 100
                )}%)`
              );
            }
          } catch (error) {
            console.error("Breed detection failed:", error);
            // Don't show error to user - just continue without breed detection
          } finally {
            setIsDetectingBreed(false);
          }
        }
      }
    } catch (error) {
      console.error("Photo upload failed:", error);
      alert(`Failed to upload photo: ${error.message}`);
      event.target.value = ""; // Clear the input
    }
  };

  const removePhoto = (photoType) => {
    setRegistrationData((prev) => ({
      ...prev,
      photos: {
        ...prev.photos,
        [photoType]: {
          ...prev.photos[photoType],
          file: null,
          preview: null,
          isDng: false,
          fileName: null,
          fileSize: null,
        },
      },
    }));
  };

  const getRequiredPhotosCount = () => {
    return Object.values(registrationData.photos).filter(
      (photo) => photo.required && photo.file
    ).length;
  };

  const getTotalRequiredPhotos = () => {
    return Object.values(registrationData.photos).filter(
      (photo) => photo.required
    ).length;
  };

  // Helper function to generate small thumbnail for instant avatar loading

  // Helper function to upload images to Firebase Storage
  const uploadImagesToStorage = async (photos) => {
    const uploadPromises = [];

    for (const [photoType, photoData] of Object.entries(photos)) {
      if (photoData.file) {
        const uploadPromise = (async () => {
          try {
            console.log(`Uploading ${photoType} to storage...`);

            // Create unique filename
            const timestamp = Date.now();
            const filename = `pet-registration/${user.uid}/${timestamp}-${photoType}.jpg`;
            const storageRef = ref(storage, filename);

            // Upload file
            const snapshot = await uploadBytes(storageRef, photoData.file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            console.log(`✅ ${photoType} uploaded successfully`);
            return {
              type: photoType,
              url: downloadURL,
              required: photoData.required,
            };
          } catch (error) {
            console.error(`❌ Failed to upload ${photoType}:`, error);
            throw new Error(`Failed to upload ${photoType}: ${error.message}`);
          }
        })();

        uploadPromises.push(uploadPromise);
      }
    }

    // Wait for all uploads to complete
    const results = await Promise.all(uploadPromises);
    return results;
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!registrationData.name || !registrationData.photos.frontFace.file)
      return;

    setIsRegistering(true);
    try {
      console.log(
        "Starting pet registration with URL-based biometric processing...",
        {
          user: user?.uid,
          userEmail: user?.email,
          isLoggedIn: !!user,
          authCurrentUser: auth.currentUser?.uid,
          name: registrationData.name,
        }
      );

      // Ensure user is authenticated
      if (!user || !auth.currentUser) {
        throw new Error("User not authenticated. Please log in again.");
      }

      // Step 1: Upload all images to Firebase Storage
      console.log("📤 Uploading images to storage...");
      const imageUrls = await uploadImagesToStorage(registrationData.photos);

      if (imageUrls.length === 0) {
        throw new Error("No images could be uploaded for registration");
      }

      console.log(
        `✅ Successfully uploaded ${imageUrls.length} images to storage`
      );

      // Step 2: Generate biometric ID using the new URL-based function
      console.log("🔬 Generating biometric ID from uploaded images...");
      const generateBiometricFn = httpsCallable(
        functions,
        "generatePetBiometricV2"
      );
      const biometricResult = await generateBiometricFn({
        imageUrls: imageUrls,
        petData: {
          species: registrationData.species,
          breed: registrationData.breed,
          birthDate: registrationData.birthDate,
          gender: registrationData.gender,
        },
      });

      if (!biometricResult.data.success) {
        throw new Error(
          "Failed to generate biometric ID: " + biometricResult.data.message
        );
      }

      console.log(
        "✅ Biometric ID generated:",
        biometricResult.data.biometricID.fingerprint,
        `(Confidence: ${Math.round(
          biometricResult.data.biometricID.confidence * 100
        )}%)`
      );

      // Step 3: Register the pet with the biometric profile
      console.log("📝 Registering pet with biometric profile...");
      const registerPetFn = httpsCallable(functions, "registerPet");

      // Use the first uploaded image as the main photo for registration

      const result = await registerPetFn({
        name: registrationData.name,
        species: registrationData.species,
        breed: registrationData.breed || "Unknown",
        birthDate: registrationData.birthDate || "",
        gender: registrationData.gender || "",
        description:
          registrationData.description ||
          `${registrationData.species} registered via dashboard`,
        contactInfo: {
          ownerName: user.displayName || user.email || "Unknown",
          ownerEmail: user.email || "",
          phone: "Not provided",
        },
        biometricProfile: biometricResult.data.biometricID,
        imageUrls: imageUrls, // Include all image URLs for future reference
      });

      console.log("Registration result:", result.data);

      if (result.data.success) {
        const petId = result.data.petId;

        // Reset form
        setRegistrationData({
          name: "",
          species: "Dog",
          birthDate: "",
          gender: "",
          description: "",
          breed: "",
          breedConfidence: 0,
          photos: {
            frontFace: { file: null, preview: null, required: true },
            leftProfile: { file: null, preview: null, required: true },
            rightProfile: { file: null, preview: null, required: true },
            fullBody: { file: null, preview: null, required: false },
            distinctiveMarks: { file: null, preview: null, required: false },
          },
        });

        // Notify parent component
        if (onPetRegistered) {
          onPetRegistered();
        }

        // Call onComplete for modal mode
        if (onComplete) {
          onComplete();
        } else {
          alert(
            `🎉 ${registrationData.name} has been registered successfully!\nPet ID: ${petId}`
          );
        }
      } else {
        throw new Error(result.data.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration failed:", error);
      alert(`Failed to register pet: ${error.message}. Please try again.`);
    } finally {
      setIsRegistering(false);
    }
  };

  const photoLabels = {
    frontFace: "Front Face (Required)",
    leftProfile: "Left Profile (Required)",
    rightProfile: "Right Profile (Required)",
    fullBody: "Full Body (Optional)",
    distinctiveMarks: "Distinctive Marks (Optional)",
  };

  return (
    <div className="space-y-6">
      <h2
        className={`text-xl lg:text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
      >
        <Plus
          className={`w-6 h-6 lg:w-8 lg:h-8 ${themeClasses.text.accent} mr-3`}
        />
        Register New Pet
      </h2>

      <form onSubmit={handleRegistrationSubmit} className="space-y-6">
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-8`}
        >
          {/* Enhanced Multi-Photo Upload Section */}
          <div className="mb-8">
            <h3
              className={`text-xl font-bold ${themeClasses.text.primary} mb-2 flex items-center`}
            >
              <Camera className={`w-6 h-6 ${themeClasses.text.accent} mr-2`} />
              Pet Photos for Individual Recognition
            </h3>

            {/* File Format Notice */}
            <div
              className={`${themeClasses.bg.secondary} border ${themeClasses.border.secondary} rounded-lg p-3 mb-4`}
            >
              <p className={`text-sm ${themeClasses.text.secondary}`}>
                Upload clear, well-lit photos of your pet for the best AI breed
                detection results.
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className={`${themeClasses.text.secondary} text-sm`}>
                  Upload Progress: {getRequiredPhotosCount()}/
                  {getTotalRequiredPhotos()} required photos
                </span>
                <span className={`${themeClasses.text.accent} text-sm`}>
                  {Math.round(
                    (getRequiredPhotosCount() / getTotalRequiredPhotos()) * 100
                  )}
                  % Complete
                </span>
              </div>
              <div
                className={`w-full ${themeClasses.bg.tertiary} rounded-full h-2`}
              >
                <div
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (getRequiredPhotosCount() / getTotalRequiredPhotos()) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Photo Upload Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(registrationData.photos).map(
                ([photoType, photoData]) => (
                  <div key={photoType} className="space-y-3">
                    <h4
                      className={`${themeClasses.text.primary} font-medium flex items-center`}
                    >
                      {photoLabels[photoType]}
                      {photoData.required && (
                        <span className={`${themeClasses.text.error} ml-1`}>
                          *
                        </span>
                      )}
                    </h4>

                    {/* AI Breed Detection Status - More Prominent */}
                    {photoType === "frontFace" && isDetectingBreed && (
                      <div
                        className={`${themeClasses.bg.accent}/10 border ${themeClasses.border.accent} rounded-lg p-3 mb-3`}
                      >
                        <div className="flex items-center space-x-3">
                          <Loader
                            className={`w-5 h-5 ${themeClasses.text.accent} animate-spin`}
                          />
                          <div>
                            <p
                              className={`${themeClasses.text.accent} font-medium text-sm`}
                            >
                              🤖 AI Analyzing Breed...
                            </p>
                            <p
                              className={`${themeClasses.text.secondary} text-xs`}
                            >
                              Our AI is identifying your dog's breed from the
                              photo
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="relative">
                      {photoData.file ? (
                        <div className="relative">
                          {photoData.isDng ? (
                            // DNG file - show preview if available, otherwise show placeholder
                            photoData.preview ? (
                              <div className="relative">
                                <img
                                  src={photoData.preview}
                                  alt={photoLabels[photoType]}
                                  className={`w-full h-48 object-cover rounded-lg border-2 ${themeClasses.border.accent}`}
                                />
                                {/* Removed DNG badge - not relevant to users */}
                                <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                                  {photoData.fileName} •{" "}
                                  {(photoData.fileSize / (1024 * 1024)).toFixed(
                                    1
                                  )}{" "}
                                  MB
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`w-full h-48 ${themeClasses.bg.secondary} rounded-lg border-2 ${themeClasses.border.accent} flex flex-col items-center justify-center`}
                              >
                                <CheckCircle
                                  className={`w-12 h-12 ${themeClasses.text.accent} mb-2`}
                                />
                                <span
                                  className={`${themeClasses.text.primary} font-medium`}
                                >
                                  ✅ RAW File Ready
                                </span>
                                <span
                                  className={`${themeClasses.text.secondary} text-sm text-center px-2`}
                                >
                                  {photoData.fileName}
                                </span>
                                <span
                                  className={`${themeClasses.text.accent} text-xs mt-1 text-center px-2`}
                                >
                                  High-quality RAW processing enabled
                                </span>
                                <span
                                  className={`${themeClasses.text.muted} text-xs mt-1`}
                                >
                                  {(photoData.fileSize / (1024 * 1024)).toFixed(
                                    1
                                  )}{" "}
                                  MB
                                </span>
                              </div>
                            )
                          ) : (
                            // Regular image preview
                            <img
                              src={photoData.preview}
                              alt={photoLabels[photoType]}
                              className={`w-full h-48 object-cover rounded-lg border-2 ${themeClasses.border.accent}`}
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removePhoto(photoType)}
                            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          {photoType === "frontFace" &&
                            registrationData.breed &&
                            !photoData.isDng && (
                              <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                                {registrationData.breed} (
                                {Math.round(
                                  registrationData.breedConfidence * 100
                                )}
                                %)
                              </div>
                            )}
                          {/* Show breed detection for DNG files too */}
                          {photoType === "frontFace" &&
                            registrationData.breed &&
                            photoData.isDng &&
                            photoData.preview && (
                              <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                                {registrationData.breed} (
                                {Math.round(
                                  registrationData.breedConfidence * 100
                                )}
                                %)
                              </div>
                            )}
                        </div>
                      ) : (
                        <div
                          className={`border-2 border-dashed ${themeClasses.border.secondary} rounded-lg p-8 text-center hover:${themeClasses.border.accent} transition-colors`}
                        >
                          <input
                            type="file"
                            accept="image/*,.dng,.cr2,.nef,.arw"
                            onChange={(e) =>
                              handleEnhancedPhotoUpload(e, photoType)
                            }
                            className="hidden"
                            id={`photo-${photoType}`}
                          />
                          <label
                            htmlFor={`photo-${photoType}`}
                            className="cursor-pointer flex flex-col items-center space-y-2"
                          >
                            <Upload
                              className={`w-12 h-12 ${themeClasses.text.tertiary}`}
                            />
                            <span className={themeClasses.text.tertiary}>
                              Click to upload
                            </span>
                            <span
                              className={`text-xs ${themeClasses.text.muted}`}
                            >
                              Photos up to 10MB
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pet Name */}
            <div>
              <label
                className={`block ${themeClasses.text.primary} font-semibold mb-2`}
              >
                Pet Name *
              </label>
              <input
                value={registrationData.name}
                onChange={(e) =>
                  setRegistrationData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                className={`w-full p-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:${themeClasses.border.accent} focus:outline-none`}
                placeholder="Enter your pet's name"
                required
                disabled={isRegistering}
              />
            </div>

            {/* Species */}
            <div>
              <label
                className={`block ${themeClasses.text.primary} font-semibold mb-2`}
              >
                Species
              </label>
              <select
                value={registrationData.species}
                onChange={(e) =>
                  setRegistrationData((prev) => ({
                    ...prev,
                    species: e.target.value,
                  }))
                }
                className={`w-full p-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:${themeClasses.border.accent} focus:outline-none`}
                disabled={isRegistering}
              >
                <option value="Dog">Dog</option>
                <option value="Cat">Cat</option>
                <option value="Bird">Bird</option>
                <option value="Rabbit">Rabbit</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Birth Date */}
            <div>
              <label
                className={`block ${themeClasses.text.primary} font-semibold mb-2`}
              >
                Birth Date
              </label>
              <input
                type="date"
                value={registrationData.birthDate}
                onChange={(e) =>
                  setRegistrationData((prev) => ({
                    ...prev,
                    birthDate: e.target.value,
                  }))
                }
                className={`w-full p-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:${themeClasses.border.accent} focus:outline-none`}
                disabled={isRegistering}
              />
              {/* Gender */}
              <div>
                <label
                  className={`block ${themeClasses.text.primary} font-semibold mb-2`}
                >
                  Gender
                </label>
                <select
                  value={registrationData.gender}
                  onChange={(e) =>
                    setRegistrationData((prev) => ({
                      ...prev,
                      gender: e.target.value,
                    }))
                  }
                  className={`w-full p-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:${themeClasses.border.accent} focus:outline-none`}
                  disabled={isRegistering}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
            </div>

            {/* Breed */}
            <div>
              <label
                className={`block ${themeClasses.text.primary} font-semibold mb-2`}
              >
                Breed{" "}
                {registrationData.breed && (
                  <span className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 py-1 rounded-full text-xs font-medium ml-2">
                    🎯 {Math.round(registrationData.breedConfidence * 100)}%
                    confidence
                  </span>
                )}
              </label>
              <input
                type="text"
                value={registrationData.breed}
                onChange={(e) =>
                  setRegistrationData((prev) => ({
                    ...prev,
                    breed: e.target.value,
                  }))
                }
                className={`w-full p-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:${themeClasses.border.accent} focus:outline-none`}
                placeholder="Will be detected from front face photo"
                disabled={isRegistering}
              />
            </div>
          </div>

          {/* Description */}
          <div className="mt-6">
            <label
              className={`block ${themeClasses.text.primary} font-semibold mb-2`}
            >
              Description
            </label>
            <textarea
              value={registrationData.description}
              onChange={(e) =>
                setRegistrationData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className={`w-full p-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:${themeClasses.border.accent} focus:outline-none h-24 resize-none`}
              placeholder="Tell us about your pet's personality, habits, or special characteristics..."
              disabled={isRegistering}
            />
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={
                !registrationData.name ||
                !registrationData.photos.frontFace.file ||
                isRegistering
              }
              className={`${themeClasses.bg.button} disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 flex items-center mx-auto`}
            >
              {isRegistering ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Registering Pet...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Register My Pet
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegisterPetPage;
